import {useCallback, useEffect, useState} from "react";
import {notify} from "../utils/toast";
import {get} from "aws-amplify/api";
import {Card, CardHeader, CardBody, CardTitle, Row, Col, Button, ModalHeader, Modal, ModalBody} from "reactstrap";
import {FormGroup, Input, InputGroup} from "reactstrap";
import {BackgroundColorContext} from "../contexts/BackgroundColorContext";
// import Select from "react-select"; // Replaced with reactstrap Input
import {useForm} from "react-hook-form";
import {Worlds} from "@zwiftalizer/parser/lib/esm/types/worlds";
import BenchmarksCollection from "../components/Benchmarks/BenchmarksCollection";
import {RingLoader} from "react-spinners";
import {useParams} from "react-router";
import awsconfig from "../configs/awsconfig.json";
import {getWorldName, titleCase} from "../utils/functions";
import ReactGA from "react-ga4";
import {GoogleAnalyticsEventCategories} from "../constants";
import PageHeader from "../components/Common/PageHeader";

const worldOptions: any[] = [];

const profileOptions: any[] = [
  {label: "Ultra", value: "ultra"},
  {label: "High", value: "high"},
  {label: "Medium", value: "medium"},
  {label: "Basic", value: "basic"}
];

const resolutionOptions: any[] = [
  {label: "2160 (4K)", value: "2160"},
  {label: "1440 (WQHD)", value: "1440"},
  {label: "1080 (FHD)", value: "1080"},
  {label: "864 (iPhone 11)", value: "864"},
  {label: "750 (iPhone 7)", value: "750"},
  {label: "720 (HD)", value: "720"},
  {label: "576 (SD)", value: "576"}
];

const crowdOptions: any[] = [
  {label: "Fondo (80-100%)", value: "100"},
  {label: "Packed (60-80%)", value: "080"},
  {label: "Crowded (40-60%)", value: "060"},
  {label: "Company (20-40%)", value: "040"},
  {label: "Solo (0-20%)", value: "020"}
];

Worlds.forEach((label: string, value: number) => {
  value > 0 &&
    worldOptions.push({
      label,
      value: `${value}`
    });
});

const getCurrentQuarter = () => {
  const month = new Date().getMonth() + 1;
  return Math.ceil(month / 3);
};

const currentYear = new Date().getFullYear();
const currentQuarter = getCurrentQuarter();

// Determine the initial year and quarter when the page loads
let initialYear = currentYear;
let initialQuarter = currentQuarter - 1;

if (initialQuarter === 0) {
  initialQuarter = 4; // Set to Q4 if the current quarter is Q1
  initialYear = currentYear - 1; // Set to the previous year
}

//const yearOptions = [{label: "All", value: "0"}].concat(
const yearOptions = Array.from({length: currentYear - 2020}, (v, k) => currentYear - k).map((year) => ({
  label: year.toString(),
  value: year.toString()
}));

const quarterOptions = [
  {label: "Q4", value: "4"},
  {label: "Q3", value: "3"},
  {label: "Q2", value: "2"},
  {label: "Q1", value: "1"}
];

const Benchmarks = () => {
  const {world = "9"} = useParams<{world: string}>();
  const {profile = "ultra"} = useParams<{profile: string}>();
  const {resolution = "2160"} = useParams<{resolution: string}>();
  const {crowd = "100"} = useParams<{crowd: string}>();
  const {year = `${initialYear}`} = useParams<{year: string}>();
  const {quarter = `${initialQuarter}`} = useParams<{quarter: string}>();

  const initialSelectedWorld = worldOptions.filter((x: any) => x.value === world)[0];
  const initialSelectedProfile = profileOptions.filter((x: any) => x.value === profile)[0];
  const initialSelectedResolution = resolutionOptions.filter((x: any) => x.value === resolution)[0];
  const initialSelectedCrowd = crowdOptions.filter((x: any) => x.value === crowd)[0];
  const initialSelectedYear = yearOptions.filter((x: any) => x.value === year)[0];
  const initialSelectedQuarter = quarterOptions.filter((x: any) => x.value === quarter)[0];

  const {setValue, getValues} = useForm();
  const [selectedWorld, setSelectedWorld] = useState(initialSelectedWorld);
  const [selectedProfile, setSelectedProfile] = useState(initialSelectedProfile);
  const [selectedResolution, setSelectedResolution] = useState(initialSelectedResolution);
  const [selectedCrowd, setSelectedCrowd] = useState(initialSelectedCrowd);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [selectedYear, setSelectedYear] = useState(initialSelectedYear);
  const [selectedQuarter, setSelectedQuarter] = useState(initialSelectedQuarter);
  const [benchmarksData, setBenchmarksData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalLink, setModalLink] = useState(false);
  const [link, setLink] = useState("");
  const [dynamicQuarterOptions, setDynamicQuarterOptions] = useState(quarterOptions);

  const handleChangeWorld = async (selectedOption: any) => {
    setValue("selectedWorld", selectedOption);
    setSelectedWorld(selectedOption);
    await getData();
  };

  const handleChangeProfile = async (selectedOption: any) => {
    setValue("selectedProfile", selectedOption);
    setSelectedProfile(selectedOption);
    await getData();
  };

  const handleChangeResolution = async (selectedOption: any) => {
    setValue("selectedResolution", selectedOption);
    setSelectedResolution(selectedOption);
    await getData();
  };

  const handleChangeCrowd = async (selectedOption: any) => {
    setValue("selectedCrowd", selectedOption);
    setSelectedCrowd(selectedOption);
    await getData();
  };

  // const handleChangeYear = async (selectedOption: any) => {
  //   setValue("selectedYear", selectedOption);
  //   setSelectedYear(selectedOption);
  //   await getData();
  // };

  const handleChangeYear = async (selectedOption: any) => {
    setValue("selectedYear", selectedOption);
    setSelectedYear(selectedOption);

    // If the selected year is the current year and the selected quarter is in the future,
    // change the selected quarter to the current quarter
    if (parseInt(selectedOption.value) === currentYear) {
      const selectedQuarterValue = parseInt(getValues("selectedQuarter").value);
      if (selectedQuarterValue > currentQuarter) {
        const currentQuarterOption = quarterOptions.find((option) => parseInt(option.value) === currentQuarter);
        setValue("selectedQuarter", currentQuarterOption);
        // Check if currentQuarterOption is not undefined before setting it
        if (currentQuarterOption) {
          setValue("selectedQuarter", currentQuarterOption);
          setSelectedQuarter(currentQuarterOption);
        } else {
          // Handle the case where currentQuarterOption is undefined
          // For example, set to the first available option or handle the error
          setSelectedQuarter(quarterOptions[0]);
          setValue("selectedQuarter", quarterOptions[0]);
        }
      }
    }

    await getData();
  };

  const handleChangeQuarter = async (selectedOption: any) => {
    setValue("selectedQuarter", selectedOption);
    setSelectedQuarter(selectedOption);
    await getData();
  };

  const toggleAdvancedOptions = () => setShowAdvancedOptions(!showAdvancedOptions);

  const toggleModalLink = () => {
    // if it's currently false, then we are about to show
    if (!modalLink) {
      setTimeout(() => copyToClipBoard(link), 500);
    }
    setModalLink(!modalLink);
  };

  // Notification function is now imported from utils/toast

  async function copyToClipBoard(link: string) {
    try {
      ReactGA.event({
        category: GoogleAnalyticsEventCategories.BENCHMARKS,
        action: "get permalink",
        label: `${link}`
      });
      await navigator.clipboard.writeText(link);
      notify("Link copied to clipboard");
    } catch {
      // console.log(err);
    }
  }

  const handleClickToOpen = (link: string, title: string): void => {
    window.open(link, title);
  };

  async function getData() {
    try {
      let path: string = `/benchmarks/${getValues("selectedWorld").value}/${getValues("selectedProfile").value}/${
        getValues("selectedResolution").value
      }/${getValues("selectedCrowd").value}`;

      if (getValues("selectedYear").value !== "0") {
        path += `/${getValues("selectedYear").value}`;
      }

      if (getValues("selectedQuarter").value !== "0") {
        path += `/${getValues("selectedQuarter").value}`;
      }

      ReactGA.event({
        category: GoogleAnalyticsEventCategories.BENCHMARKS,
        action: "get data",
        label: `${path}`
      });

      const link: string = `${awsconfig.Environment.hostname}${path}`;
      setLink(link);
      //setBenchmarksData([]);
      setLoading(true);
      //console.log(path);
      const {body} = await get({
        apiName: "data",
        path: path
      }).response;
      const data = (await body.json()) as any;
      //console.log(JSON.stringify(data));
      setLoading(false);
      setBenchmarksData(data !== undefined && data !== null && data.length ? data : []);
    } catch {
      setLoading(false);
      //console.error(`error getting benchmarks data: ${err}`);
      return undefined;
    }
  }

  const loadData = useCallback(getData, [getValues]);

  useEffect(() => {
    async function initialLoad() {
      setValue("selectedWorld", initialSelectedWorld);
      setValue("selectedProfile", initialSelectedProfile);
      setValue("selectedResolution", initialSelectedResolution);
      setValue("selectedCrowd", initialSelectedCrowd);
      setValue("selectedYear", initialSelectedYear);
      setValue("selectedQuarter", initialSelectedQuarter);
      await loadData();
    }
    initialLoad();
  }, []);

  // Update dynamicQuarterOptions based on the selected year
  useEffect(() => {
    if (parseInt(selectedYear.value) === currentYear) {
      const updatedQuarterOptions = quarterOptions.map((option) => {
        return {
          ...option,
          isDisabled: parseInt(option.value) > currentQuarter
        };
      });
      setDynamicQuarterOptions(updatedQuarterOptions);
    } else {
      setDynamicQuarterOptions(quarterOptions);
    }
  }, [selectedYear]);

  const newWindowTitle: string = `${getWorldName(selectedWorld.value)} / ${titleCase(selectedProfile.value)} / ${
    selectedResolution.value
  } / ${selectedCrowd.value} / ${selectedYear.value} / ${selectedQuarter.value}`;

  return (
    <BackgroundColorContext.Consumer>
      {({color}) => (
        <>
          <PageHeader
            title="Performance Benchmarks"
            subtitle="Compare FPS performance across different worlds and profiles"
            icon="fa fa-chart-line"
          />
          <Row className="card-blank">
            <Col md="6">
              <div className="float-left">
                {!showAdvancedOptions && (
                  <Button color="primary" data-color={color} onClick={toggleAdvancedOptions}>
                    Show Advanced Options
                  </Button>
                )}
                {showAdvancedOptions && (
                  <Button color="secondary" data-color={color} onClick={toggleAdvancedOptions}>
                    Hide Advanced Options
                  </Button>
                )}
              </div>
            </Col>
            <Col md="6">
              <div className="float-right">
                <Button color="primary" data-color={color} onClick={toggleModalLink}>
                  Get Link <i className="fa fa-link" />
                </Button>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md="12">
              <Card>
                <CardBody>
                  <Row>
                    <Col md="4">
                      <h4>World</h4>
                    </Col>
                    <Col md="4">
                      <h4>Profile</h4>
                    </Col>
                    <Col md="4">
                      <h4>Resolution</h4>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <Input
                          type="select"
                          className="form-control"
                          data-color={color}
                          value={selectedWorld?.value || ""}
                          onChange={(e) => {
                            const selectedOption = worldOptions.find((opt) => opt.value === e.target.value);
                            handleChangeWorld(selectedOption);
                          }}
                        >
                          {worldOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Input
                          type="select"
                          className="form-control"
                          data-color={color}
                          value={selectedProfile?.value || ""}
                          onChange={(e) => {
                            const selectedOption = profileOptions.find((opt) => opt.value === e.target.value);
                            handleChangeProfile(selectedOption);
                          }}
                        >
                          {profileOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Input
                          type="select"
                          className="form-control"
                          data-color={color}
                          value={selectedResolution?.value || ""}
                          onChange={(e) => {
                            const selectedOption = resolutionOptions.find((opt) => opt.value === e.target.value);
                            handleChangeResolution(selectedOption);
                          }}
                        >
                          {resolutionOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                  {showAdvancedOptions && (
                    <>
                      <Row>
                        <Col md="4">
                          <h4>Crowdedness</h4>
                        </Col>
                        <Col md="4">
                          <h4>Year</h4>
                        </Col>
                        <Col md="4">
                          <h4>Quarter</h4>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="4">
                          <FormGroup>
                            <Input
                              type="select"
                              className="form-control"
                              data-color={color}
                              value={selectedCrowd?.value || ""}
                              onChange={(e) => {
                                const selectedOption = crowdOptions.find((opt) => opt.value === e.target.value);
                                handleChangeCrowd(selectedOption);
                              }}
                            >
                              {crowdOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <FormGroup>
                            <Input
                              type="select"
                              className="form-control"
                              data-color={color}
                              value={selectedYear?.value || ""}
                              onChange={(e) => {
                                const selectedOption = yearOptions.find((opt) => opt.value === e.target.value);
                                handleChangeYear(selectedOption);
                              }}
                            >
                              {yearOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <FormGroup>
                            <Input
                              type="select"
                              className="form-control"
                              data-color={color}
                              value={selectedQuarter?.value || ""}
                              onChange={(e) => {
                                const selectedOption = dynamicQuarterOptions.find(
                                  (opt) => opt.value === e.target.value
                                );
                                handleChangeQuarter(selectedOption);
                              }}
                            >
                              {dynamicQuarterOptions.map((option) => (
                                <option
                                  key={option.value}
                                  value={option.value}
                                  disabled={(option as any).isDisabled || false}
                                >
                                  {option.label}
                                </option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>
                      </Row>
                    </>
                  )}

                  <Row>
                    <Col md="12">
                      <BenchmarksCollection
                        items={benchmarksData}
                        world={selectedWorld.value}
                        profile={selectedProfile.value}
                        resolution={selectedResolution.value}
                        crowd={selectedCrowd.value}
                        year={selectedYear.value}
                        quarter={selectedQuarter.value}
                        showLinks={true}
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
          {loading && (
            <div className="iconWrapper">
              <RingLoader color="#4682b4" size={80} loading={true} />
            </div>
          )}

          <Modal modalClassName="modal-share" isOpen={modalLink} toggle={toggleModalLink}>
            <ModalHeader>
              <button aria-label="Close" className="close" onClick={toggleModalLink}>
                <i className="fa fa-times" />
              </button>
            </ModalHeader>
            <ModalBody>
              <Card>
                <CardHeader data-color={color}>
                  <CardTitle tag="h3">Link</CardTitle>
                  <h5 className="card-category">Permalink to benchmarks for {newWindowTitle}</h5>
                </CardHeader>
                <CardBody>
                  <InputGroup>
                    <Input
                      type="text"
                      readOnly={true}
                      name="shareLink"
                      id="shareLink"
                      placeholder={link}
                      value={link}
                    />

                    <Button
                      color="primary"
                      data-color={color}
                      onClick={() => {
                        copyToClipBoard(link);
                      }}
                    >
                      <i className="fa fa-copy" />
                    </Button>
                  </InputGroup>
                  <Button color="primary" data-color={color} onClick={() => handleClickToOpen(link, newWindowTitle)}>
                    Open in new Tab
                  </Button>
                  <Button color="primary" data-color={color} onClick={toggleModalLink}>
                    Close
                  </Button>
                </CardBody>
              </Card>
            </ModalBody>
          </Modal>
        </>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default Benchmarks;
